import React, { KeyboardEventHandler, Ref, useEffect } from 'react';
import { Grid, IconButton, InputAdornment, SxProps } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { Search as SearchIcon, Times } from '@assets/svgs';
import { TextField } from '@components/textField';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

export type SearchFieldProps = {
  onChange: (value: string) => void;
  defaultSearchValue?: any;
  isClearable?: boolean;
  placeholder?: string;
  onPressEndIcon?: () => void;
  autofocus?: boolean;
  sx?: SxProps;
  variant?: 'standard' | 'rounded';
  label?: string;
  disabled?: boolean;
  /** if the value is being controlled outside of this component */
  overrideValue?: string;
  size?: 'small' | 'medium';
  helperText?: string;
  /** Ref to the underlying input, e.g. to focus it from a keyboard shortcut. */
  inputRef?: Ref<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
};

export const SearchField = ({
  onChange,
  overrideValue,
  defaultSearchValue,
  isClearable = true,
  placeholder = 'Search',
  autofocus = false,
  onPressEndIcon,
  sx,
  variant = 'rounded',
  label,
  disabled,
  size = 'medium',
  helperText,
  inputRef,
  onKeyDown,
}: SearchFieldProps) => {
  const [value, setValue] = React.useState(defaultSearchValue || '');
  const debounced = useDebouncedCallback((value) => {
    onChange(value);
    // NOTE: intentionally do not trim here, if a user types a space and is typing slow it is getting removed, use trim in the wrapper
  }, DEBOUNCE_DELAYS.TYPING);

  const handleClearText = () => {
    debounced.cancel();
    setValue('');
    onChange('');

    onPressEndIcon?.();
  };

  useEffect(() => {
    if (overrideValue !== value && overrideValue !== undefined) {
      debounced.cancel();
      setValue(overrideValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideValue]);

  return (
    <Grid id='search-bar'>
      <TextField
        autoFocus={autofocus}
        inputRef={inputRef}
        onKeyDown={onKeyDown}
        size={size}
        label={label}
        name='Search'
        aria-label='searchbar'
        title='searchbar'
        hiddenLabel
        disabled={disabled}
        helperText={helperText}
        fullWidth
        id='outlined-search'
        slotProps={{
          htmlInput: {
            'aria-label': 'Search',
          },
          input: {
            endAdornment:
              isClearable && value ? (
                <IconButton
                  aria-label='Clear text'
                  title='clear text'
                  onClick={handleClearText}
                  size='small'
                  sx={{
                    position: 'absolute',
                    right: 4,
                  }}
                >
                  <Times width='10px' height='10px' />
                </IconButton>
              ) : null,
          },
        }}
        startIcon={
          <InputAdornment position='start' sx={{ marginLeft: 1, marginRight: '2px' }}>
            <SearchIcon color={disabled ? 'text.disabled' : 'text.main'} width='16px' height='15px' />
          </InputAdornment>
        }
        onChange={(e) => {
          setValue(e.target.value);
          debounced(e.target.value);
        }}
        placeholder={placeholder}
        value={value}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: variant === 'rounded' ? '18px' : 'auto',
          },
          ...sx,
        }}
        data-testid={`search-field-${label}`}
      />
    </Grid>
  );
};
